import BasePlayer from './BasePlayer';
import { getNextPlayRequestId } from '../common/channelPlayerState';

/**
 * LG webOS player implementation
 * Uses Luna service API (window.webOS.service)
 * Note: webOS UDP streams are always fullscreen
 */
class WebOSPlayer extends BasePlayer {
  constructor() {
    super();
    this.maxRetries = 3;
  }

  supportsFullscreenToggle() {
    return false; // webOS streams are always fullscreen
  }

  async play(url, isFullScreen = true) {
    const playerState = this.getPlayerState();
    this.updatePlayerState({ url });

    const myRequestId = getNextPlayRequestId();

    if (playerState.isPlayerBusy) {
      console.warn('webOS: Player is currently busy. Aborting this call.');
      return;
    }

    this.updatePlayerState({ isPlayerBusy: true });

    try {
      await this._playWithRetry(myRequestId, 0);
    } finally {
      const finalCheckState = this.getPlayerState();
      if (myRequestId === finalCheckState.playRequestId) {
        this.updatePlayerState({ isPlayerBusy: false });
      }
    }
  }

  async _playWithRetry(myRequestId, retryCount) {
    const playerState = this.getPlayerState();

    console.log('webOS: webosDevicePlayer called', {
      url: playerState.url,
      retryCount,
      myRequestId,
      playRequestId: playerState.playRequestId,
    });

    if (!window.webOS || !window.webOS.service) {
      console.warn('webOS: webOS service not available');
      throw new Error('webOS service not available');
    }

    try {
      // Unload previous media if any
      if (playerState.webosMediaId) {
        await this._unloadMedia(playerState.webosMediaId);
        this.updatePlayerState({ webosMediaId: null });
      }

      // Load new media
      const mediaId = await this._loadMedia(playerState.url, myRequestId, retryCount);
      
      if (!mediaId) {
        throw new Error('Failed to load media - no mediaId returned');
      }

      // Play the media
      await this._playMedia(mediaId, myRequestId, retryCount);

    } catch (err) {
      console.error('webOS: playback exception:', err);

      if (retryCount < this.maxRetries) {
        console.warn(`webOS: Retrying... (${retryCount + 1}/${this.maxRetries})`);
        await this.delay(500);
        return this._playWithRetry(myRequestId, retryCount + 1);
      } else {
        console.error('webOS: Maximum retry attempts reached');
        throw err;
      }
    }
  }

  _unloadMedia(mediaId) {
    return new Promise((resolve) => {
      try {
        console.log('webOS: unloading previous media', mediaId);
        window.webOS.service.request('luna://com.webos.media', {
          method: 'unload',
          parameters: { mediaId },
          onSuccess: () => {
            console.log('webOS: previous media unloaded');
            resolve();
          },
          onFailure: () => {
            // Don't block on unload failure
            resolve();
          },
        });
      } catch (e) {
        resolve();
      }
    });
  }

  _loadMedia(url, myRequestId, retryCount) {
    return new Promise((resolve, reject) => {
      console.log('webOS: sending load request for fullscreen playback', { uri: url });
      
      window.webOS.service.request('luna://com.webos.media', {
        method: 'load',
        parameters: {
          uri: url,
          type: 'media',
          payload: {
            option: {
              mediaTransportType: 'STREAMING',
              fullScreen: true,
            },
          },
        },
        onSuccess: (args) => {
          const checkState = this.getPlayerState();
          if (myRequestId !== checkState.playRequestId) {
            console.warn('webOS: Aborting load - newer request in progress');
            return resolve(null);
          }

          console.log('webOS: load success', args);
          const mediaId = args && args.mediaId;
          this.updatePlayerState({ webosMediaId: mediaId });

          if (!mediaId) {
            console.error('webOS: load succeeded without mediaId');
            return reject(new Error('No mediaId returned'));
          }

          resolve(mediaId);
        },
        onFailure: (err) => {
          const checkState = this.getPlayerState();
          if (myRequestId !== checkState.playRequestId) {
            return resolve(null);
          }
          console.error('webOS: load failed:', err);
          reject(err);
        },
      });
    });
  }

  _playMedia(mediaId, myRequestId, retryCount) {
    return new Promise((resolve, reject) => {
      console.log('webOS: sending play request', { mediaId });
      
      window.webOS.service.request('luna://com.webos.media', {
        method: 'play',
        parameters: { mediaId },
        onSuccess: (res) => {
          const playCheckState = this.getPlayerState();
          if (myRequestId !== playCheckState.playRequestId) {
            return resolve();
          }
          console.log('webOS: native playback started in fullscreen:', res);
          console.log('webOS: Channel list should remain visible over fullscreen video');
          resolve();
        },
        onFailure: (err) => {
          const failCheckState = this.getPlayerState();
          if (myRequestId !== failCheckState.playRequestId) {
            return resolve();
          }
          console.error('webOS: native play failed:', err);
          reject(err);
        },
      });
    });
  }

  async stop() {
    const playerState = this.getPlayerState();
    if (!window.webOS || !window.webOS.service || !playerState.webosMediaId) {
      return;
    }

    try {
      console.log('webOS: unload request', { mediaId: playerState.webosMediaId });
      await this._unloadMedia(playerState.webosMediaId);
      this.updatePlayerState({ webosMediaId: null });
    } catch (e) {
      console.warn('webOS: error during stop:', e);
    }
  }

  async setDisplayRect(coords) {
    const playerState = this.getPlayerState();
    if (!window.webOS || !window.webOS.service || !playerState.webosMediaId) {
      console.warn('webOS: Cannot set display rect - service or mediaId not available');
      return;
    }

    const params = {
      mediaId: playerState.webosMediaId,
      x: coords.x ?? coords.left ?? 0,
      y: coords.y ?? coords.top ?? 0,
      width: coords.width,
      height: coords.height,
      fullScreen:
        (coords.x === 0 || coords.left === 0) &&
        (coords.y === 0 || coords.top === 0) &&
        coords.width === window.innerWidth &&
        coords.height === window.innerHeight,
    };

    console.log('webOS: setDisplayWindow request', params);

    return new Promise((resolve) => {
      window.webOS.service.request('luna://com.webos.media', {
        method: 'setDisplayWindow',
        parameters: params,
        onSuccess: (res) => {
          console.log('webOS: setDisplayWindow success', res);
          resolve();
        },
        onFailure: (err) => {
          console.warn('webOS: setDisplayWindow failure', err);
          // Try alternative method - setVideoDisplayWindow
          console.log('webOS: trying setVideoDisplayWindow as fallback');
          window.webOS.service.request('luna://com.webos.media', {
            method: 'setVideoDisplayWindow',
            parameters: params,
            onSuccess: (res2) => {
              console.log('webOS: setVideoDisplayWindow success', res2);
              resolve();
            },
            onFailure: (err2) => {
              console.warn('webOS: setVideoDisplayWindow also failed', err2);
              resolve(); // Don't block on failure
            },
          });
        },
      });
    });
  }

  async toggleFullscreen() {
    console.log('webOS: toggleFullscreen not supported - streams are always fullscreen');
    // webOS streams are always fullscreen, so this is a no-op
  }
}

export default WebOSPlayer;

